/**
 * Source Transformation Utilities
 *
 * Functions for transforming raw RAG sources into display-ready format
 * and various helper utilities for source handling.
 */

import type { RAGSource, DisplaySource } from '@/types/source'
import { sanitizeForTTS } from './ttsSanitize'

/**
 * Transform a raw RAGSource into a DisplaySource with computed display properties
 */
export function transformRAGSource(raw: RAGSource, index: number): DisplaySource {
  return {
    ...raw,
    id: `${raw.file_id}-${index}`,
    title: extractTitle(raw.source_filename),
    scorePercentage: Math.round(raw.score * 100),
    isExpired: isUrlExpired(raw.storage_url_expires_at),
    mediaType: getMediaType(raw.file_type, raw.source_filename, raw.original_url),
    formattedTimeRange: formatTimeRange(raw.start_time, raw.end_time),
    formattedPageNumber: formatPageNumber(raw.page_number),
  }
}

/**
 * Transform an array of RAGSources into DisplaySources, deduplicating
 * entries that share the same content. When the same passage appears as
 * both a web page (with original_url) and an extracted document (markdown
 * file from the crawl), the web page version is preferred.
 */
export function transformRAGSources(sources: RAGSource[]): DisplaySource[] {
  const transformed = sources.map((source, index) => transformRAGSource(source, index))
  return deduplicateSources(transformed)
}

/**
 * Deduplicate sources by content similarity.
 * When two sources share the same passage text (first 100 chars), keep
 * the one with the richest metadata: web > scorm > video > audio > document.
 */
function deduplicateSources(sources: DisplaySource[]): DisplaySource[] {
  const mediaTypePriority: Record<string, number> = {
    web: 5,
    scorm: 4,
    video: 3,
    audio: 2,
    image: 1,
    document: 0,
  }

  const seen = new Map<string, DisplaySource>()

  for (const source of sources) {
    // Normalize: take first 100 chars, lowercase, collapse whitespace
    const key = source.document
      .slice(0, 100)
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()

    const existing = seen.get(key)
    if (!existing) {
      seen.set(key, source)
    } else {
      // Keep the source with higher media type priority, or higher score if same type
      const existingPriority = mediaTypePriority[existing.mediaType] ?? 0
      const newPriority = mediaTypePriority[source.mediaType] ?? 0
      if (newPriority > existingPriority || (newPriority === existingPriority && source.score > existing.score)) {
        seen.set(key, source)
      }
    }
  }

  return Array.from(seen.values())
}

/**
 * Extract a human-readable title from the source filename.
 * Removes cloud_ prefix, file extensions, and hash suffixes.
 */
export function extractTitle(filename: string): string {
  return (
    filename
      // Remove cloud_ prefix
      .replace(/^cloud_/, '')
      // Remove extracted_text suffix and hash
      .replace(/_[a-f0-9]{16,}_extracted_text\.txt$/i, '')
      // Remove common file extensions
      .replace(/\.(mp4|mp3|wav|pdf|txt|docx?|pptx?|xlsx?)$/i, '')
      // Replace underscores with spaces
      .replace(/_/g, ' ')
      // Clean up multiple spaces
      .replace(/\s+/g, ' ')
      .trim() || 'Untitled Source'
  )
}

/**
 * Determine the media type from file_type, filename, and optional original_url
 */
export function getMediaType(
  fileType: string,
  filename: string,
  originalUrl?: string | null,
): 'document' | 'video' | 'audio' | 'image' | 'scorm' | 'web' {
  // SCORM packages — check file_type or original_url pattern
  if (fileType === 'scorm_package') return 'scorm'
  if (originalUrl && /\/scorm\/[^/]+\/lesson\//.test(originalUrl)) return 'scorm'

  // For transcriptions, determine media type from original filename.
  // The indexing pipeline sometimes strips file extensions (CMS uploads
  // commonly drop them), leaving the demo with a name like
  // "Camp- Human Optimization Video 7344637..." and no `.mp4` suffix.
  // Three signals tried in order:
  //   1. Extension match (highest confidence)
  //   2. Filename keyword (handles extension-stripped uploads)
  //   3. Default to a media type when neither signal hits — a
  //      transcription is by definition a transcript of media, so
  //      "audio" is a safer last-resort than "document".
  if (fileType === 'transcription') {
    // 1. Extension match
    if (filename.match(/\.(mp4|webm|mov|avi|mkv)$/i)) return 'video'
    if (filename.match(/\.(mp3|wav|m4a|ogg|flac)$/i)) return 'audio'
    // 2. Filename keyword
    if (/\bvideo\b/i.test(filename)) return 'video'
    if (/\b(audio|podcast|interview)\b/i.test(filename)) return 'audio'
    // 3. Default — transcription with no extra signal still isn't a document.
    return 'audio'
  }

  // Direct type mappings
  if (fileType === 'video' || filename.match(/\.(mp4|webm|mov|avi|mkv)$/i))
    return 'video'
  if (fileType === 'audio' || filename.match(/\.(mp3|wav|m4a|ogg|flac)$/i))
    return 'audio'
  if (
    fileType === 'image' ||
    filename.match(/\.(png|jpg|jpeg|gif|webp|svg)$/i)
  )
    return 'image'

  // Web crawl sources — documents with an HTTP(S) original_url
  if (originalUrl && /^https?:\/\//.test(originalUrl)) return 'web'

  return 'document'
}

/**
 * Format a time range for display (e.g., "3:57 - 4:09")
 */
export function formatTimeRange(
  startTime: number | null,
  endTime: number | null
): string | null {
  if (startTime === null) return null

  const formattedStart = formatSeconds(startTime)

  if (endTime === null) {
    return formattedStart
  }

  return `${formattedStart} - ${formatSeconds(endTime)}`
}

/**
 * Format a page number for display (e.g., "Page 5")
 */
export function formatPageNumber(pageNumber: number | null): string | null {
  if (pageNumber === null || pageNumber < 1) return null
  return `Page ${pageNumber}`
}

/**
 * Check if a source URL points to a PDF file.
 * Checks both the URL path and the original filename.
 */
export function isPdfUrl(url: string, filename: string): boolean {
  // Check filename extension
  if (filename.toLowerCase().endsWith('.pdf')) {
    return true
  }

  // Check URL path (before query parameters)
  try {
    const urlPath = new URL(url).pathname.toLowerCase()
    return urlPath.endsWith('.pdf')
  } catch {
    // If URL parsing fails, check string directly
    return url.toLowerCase().includes('.pdf')
  }
}

/**
 * Format seconds into MM:SS or HH:MM:SS format
 */
export function formatSeconds(seconds: number): string {
  const totalSeconds = Math.floor(seconds)
  const hours = Math.floor(totalSeconds / 3600)
  const mins = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60

  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Check if a URL expiration timestamp has passed.
 * No timestamp (proxy URLs, which never expire) means not expired —
 * `new Date(null)` is the epoch and would mark every such URL expired.
 */
export function isUrlExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false
  try {
    const expirationTime = new Date(expiresAt).getTime()
    return Date.now() > expirationTime
  } catch {
    // If we can't parse the date, assume it's not expired
    return false
  }
}

/**
 * Check if a URL will expire within the specified buffer time
 */
export function isUrlExpiringSoon(
  expiresAt: string | null,
  bufferMinutes: number = 5
): boolean {
  if (!expiresAt) return false
  try {
    const expirationTime = new Date(expiresAt).getTime()
    const bufferMs = bufferMinutes * 60 * 1000
    return Date.now() > expirationTime - bufferMs
  } catch {
    return false
  }
}

/**
 * Get the icon name for a media type (for use with Lucide icons)
 */
export function getMediaTypeIcon(
  mediaType: 'document' | 'video' | 'audio' | 'image' | 'scorm'
): string {
  const iconMap = {
    document: 'FileText',
    video: 'Video',
    audio: 'Headphones',
    image: 'Image',
    scorm: 'BookOpen',
  }
  return iconMap[mediaType]
}

/**
 * Get a color class based on relevance score percentage
 */
export function getScoreColorClass(scorePercentage: number): string {
  if (scorePercentage >= 80) return 'text-success bg-success/10'
  if (scorePercentage >= 60) return 'text-warning-strong bg-warning/10'
  return 'text-muted-foreground bg-muted'
}

/**
 * Sort sources by relevance score (highest first)
 */
export function sortSourcesByScore(sources: DisplaySource[]): DisplaySource[] {
  return [...sources].sort((a, b) => b.score - a.score)
}

/**
 * Filter sources that have expired URLs
 */
export function getExpiredSources(sources: DisplaySource[]): DisplaySource[] {
  return sources.filter((source) => source.isExpired)
}

/**
 * Filter sources that will expire soon
 */
export function getExpiringSoonSources(
  sources: DisplaySource[],
  bufferMinutes: number = 5
): DisplaySource[] {
  return sources.filter((source) =>
    isUrlExpiringSoon(source.storage_url_expires_at, bufferMinutes)
  )
}

/**
 * Sanitize text for TTS. Delegates to the canonical sanitizer (./ttsSanitize),
 * which strips source citations plus the full range of markdown/HTML/emoji
 * markup so it is never spoken aloud — not just citations, despite the name.
 */
export function sanitizeTextForTTS(text: string): string {
  return sanitizeForTTS(text)
}
